// Pure logic for the TSP Live Ranking (no React, no I/O). The server returns
// every temple with yatras in the year, each with its All India / zone /
// category / state rank (GET /tsp/ranking/temples). The views of the ICC Live
// Ranking screen are built from those rows here: a zone / category / state
// table sums the yatras of its temples; a selected zone / category / state lists
// its temples. Ranks are standard competition ranks (ties share: 1, 1, 3).

// The ICC Live Ranking's own option lists (src/pages/LiveRanking.jsx).
export const ZONES = Object.freeze(["East Zone", "West Zone", "North Zone", "South Zone"]);
export const CATEGORIES = Object.freeze(["Big Temple", "Medium Temple", "Small Temple", "Preaching Centre", "Extension Centre", "Outpost"]);
export const STATES = Object.freeze([
  "Andaman & Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra & Nagar Haveli",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
]);

// The "Filter By" choices (ICC's, without Book Wise: this ranking is not about books).
export const FILTER_BY = Object.freeze([
  { value: "", label: "All India Ranking" },
  { value: "zone", label: "Zone Wise" },
  { value: "category", label: "Category Wise" },
  { value: "state", label: "State Wise" },
]);

// "Jammu & Kashmir" = "Jammu and Kashmir": ICC's state list and its records spell some differently.
export const sameName = (a, b) =>
  String(a || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, "") ===
  String(b || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, "");

// Adds `rank` to rows sorted by yatras (most first, then by name).
export function competitionRank(rows, nameOf = (row) => row.name) {
  const sorted = [...rows].sort((a, b) => b.yatras - a.yatras || String(nameOf(a)).localeCompare(String(nameOf(b))));
  let previous = null;
  let rank = 0;
  return sorted.map((row, index) => {
    if (row.yatras !== previous) {
      rank = index + 1;
      previous = row.yatras;
    }
    return { ...row, rank };
  });
}

// One row per zone / category / state: the yatras of its temples summed.
export function groupRows(temples, field) {
  const groups = new Map();
  temples.forEach((temple) => {
    const name = temple[field];
    if (!name) return;
    const entry = groups.get(name) || { name, yatras: 0, temples: 0 };
    entry.yatras += temple.yatras;
    entry.temples += 1;
    groups.set(name, entry);
  });
  return competitionRank([...groups.values()]);
}

const templeRows = (temples) => competitionRank(temples, (row) => row.temple_name);

// What the table shows for the filters: { kind: "temples" | "groups", label, rows }.
//   filters = { filterBy: "" | "zone" | "category" | "state", zone, category, state, search: { name, category, zone } }
export function buildView(temples, filters) {
  const { filterBy = "", zone = "", category = "", state = "", search = {} } = filters || {};
  const matches = (value, text) => !text || String(value || "").toLowerCase().includes(text.trim().toLowerCase());
  const searched = (rows) => rows.filter((row) => matches(row.temple_name, search.name) && matches(row.category, search.category) && matches(row.zone, search.zone));

  if (filterBy === "zone") {
    if (!zone) return { kind: "groups", label: "Zone", rows: groupRows(temples, "zone") };
    const inZone = temples.filter((row) => row.zone === zone && (!category || row.category === category));
    return { kind: "temples", rows: searched(templeRows(inZone)) };
  }
  if (filterBy === "category") {
    if (!category) return { kind: "groups", label: "Particular", rows: groupRows(temples, "category") };
    const inCategory = temples.filter((row) => row.category === category && (!zone || row.zone === zone));
    return { kind: "temples", rows: searched(templeRows(inCategory)) };
  }
  if (filterBy === "state") {
    if (!state) return { kind: "groups", label: "State", rows: groupRows(temples, "state") };
    return { kind: "temples", rows: searched(templeRows(temples.filter((row) => sameName(row.state, state)))) };
  }
  // All India: the server's own rank.
  return { kind: "temples", rows: searched(temples.map((row) => ({ ...row, rank: row.all_rank }))) };
}

// The current table as spreadsheet rows (Download).
export function downloadRows(view) {
  if (view.kind === "groups") return view.rows.map((row) => ({ Ranking: row.rank, [view.label]: row.name, Temples: row.temples, "Number of Yatras": row.yatras }));
  return view.rows.map((row) => ({
    Ranking: row.rank,
    Particular: row.temple_name,
    Category: row.category || "",
    Zone: row.zone || "",
    State: row.state || "",
    "Number of Yatras": row.yatras,
    Completed: row.completed,
    "In Progress": row.in_progress,
  }));
}

// Compare: label rows x the chosen temples (a temple with no yatra that year reads "-").
export const COMPARE_ROWS = Object.freeze([
  { label: "Temple Name", value: (row) => row.temple_name },
  { label: "Zone", value: (row) => row.zone },
  { label: "Category", value: (row) => row.category },
  { label: "State", value: (row) => row.state },
  { label: "All India Ranking", value: (row) => row.all_rank },
  { label: "Zone Ranking", value: (row) => row.zone_rank },
  { label: "Category Ranking", value: (row) => row.category_rank },
  { label: "State Ranking", value: (row) => row.state_rank },
  { label: "Number of Yatras", value: (row) => row.yatras },
  { label: "Completed Yatras", value: (row) => row.completed },
  { label: "Yatras in Progress", value: (row) => row.in_progress },
]);

export function compareColumns(temples, selectedNames) {
  return selectedNames.map((name) => (name ? temples.find((row) => row.temple_name === name) || null : null));
}

export const display = (value) => (value === null || value === undefined || value === "" ? "-" : typeof value === "number" ? value.toLocaleString("en-IN") : String(value));
